import React, { useState } from 'react';
import { Building2, Users, Wallet, BarChart } from 'lucide-react';
import Button from '../components/common/Button';
import { useHostForm } from '../hooks/useHostForm';
import { HostFormData } from '../types/host';

const benefits = [
  {
    icon: Building2,
    title: 'Partner with DiversiCoin',
    description: 'Transform your business into a modern financial hub while generating passive revenue'
  },
  {
    icon: Wallet,
    title: 'Guaranteed Revenue',
    description: 'Earn secure, guaranteed commission on every transaction - no risk, pure profit'
  },
  {
    icon: Users,
    title: 'Premium Foot Traffic',
    description: 'Draw high-value customers who typically spend more at partner locations'
  },
  {
    icon: BarChart,
    title: 'Zero-Risk Growth',
    description: 'We handle everything: setup, maintenance, compliance, and support - you just collect the profits'
  }
];

export default function HostPage() {
  const { isSubmitting, error, success, handleSubmit } = useHostForm();
  const [formData, setFormData] = useState<HostFormData>({
    businessName: '',
    name: '',
    email: '',
    note: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await handleSubmit(formData);
    if (!error) {
      setFormData({
        businessName: '',
        name: '',
        email: '',
        note: ''
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 pt-20">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-white mb-6">Elevate Your Business with a Bitcoin ATM</h1>
          <p className="text-xl text-gray-300 mb-12">
            Generate new revenue streams with our turnkey BTM solution
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {benefits.map((benefit, index) => (
            <div key={index} className="bg-gray-800 p-6 rounded-lg">
              <benefit.icon className="w-12 h-12 text-purple-500 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">{benefit.title}</h3>
              <p className="text-gray-400">{benefit.description}</p>
            </div>
          ))}
        </div>

        <div className="max-w-xl mx-auto bg-gray-800 p-8 rounded-lg">
          <h2 className="text-2xl font-bold text-white mb-6">Get Started Today</h2>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="businessName" className="block text-gray-300 mb-2">Business Name</label>
              <input 
                id="businessName"
                name="businessName"
                type="text"
                value={formData.businessName}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                required
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label htmlFor="name" className="block text-gray-300 mb-2">Your Name</label>
              <input 
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                required
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-gray-300 mb-2">Email</label>
              <input 
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                required
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label htmlFor="note" className="block text-gray-300 mb-2">Additional Notes</label>
              <textarea
                id="note"
                name="note"
                value={formData.note}
                onChange={handleChange}
                rows={4}
                className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                disabled={isSubmitting}
              />
            </div>
            <Button 
              type="submit"
              disabled={isSubmitting}
              className="w-full"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </Button>
            {error && (
              <p className="text-red-400 text-center">{error}</p>
            )}
            {success && (
              <p className="text-green-400 text-center">Application submitted successfully!</p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}